import styled from 'styled-components';

import { CARD_BORDER, CARD_SHADOW } from './base';

// ---------------------------------------------------------------------------
// Régua de Cobrança — kanban: board com rolagem horizontal, colunas de 302px
// com corpo rolável próprio, cards arrastáveis.
//
// Cores por stage_type (UI_SPEC §Kanban) — decididas por TIPO, nunca pelo nome
// da raia (o nome é editável pelo usuário).
// ---------------------------------------------------------------------------

export const STAGE_TYPE_COLORS = {
   ENTRY: '#4098ef',
   ATTEMPT: '#8a8a8a',
   TERMINAL_PAID: '#16a34a',
   EXTERNAL: '#dc2626',
};

export const Board = styled.div`
   display: flex;
   gap: 14px;
   align-items: stretch;
   overflow-x: auto;
   padding-bottom: 8px;
   min-height: 520px;
`;

// $dropActive: realce de alvo de drop válido. TERMINAL_PAID nunca recebe o
// realce — não é alvo (a tela não seta $dropActive nela).
export const Column = styled.div`
   flex: 0 0 302px;
   width: 302px;
   display: flex;
   flex-direction: column;
   max-height: calc(100vh - 240px);
   border-radius: 12px;
   border: 1px solid
      ${({ $dropActive }) =>
         $dropActive ? 'rgba(64, 152, 239, 0.9)' : CARD_BORDER};
   background: ${({ $dropActive }) => ($dropActive ? '#f2f8fe' : '#fbfbfc')};
   box-shadow: ${CARD_SHADOW};
   transition: border-color 120ms ease, background 120ms ease;
`;

export const ColumnHead = styled.div`
   padding: 12px 14px 10px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.07);
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

export const ColumnTitleRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

export const ColumnDot = styled.span`
   width: 8px;
   height: 8px;
   border-radius: 999px;
   flex: 0 0 8px;
   background: ${({ $color }) => $color ?? '#8a8a8a'};
`;

export const ColumnName = styled.b`
   font-size: 13px;
   font-weight: 700;
   flex: 1;
   min-width: 0;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const ColumnCount = styled.span`
   font-size: 11px;
   font-weight: 800;
   color: #555;
   background: rgba(0, 0, 0, 0.06);
   border-radius: 999px;
   padding: 2px 8px;
`;

export const ColumnMetaRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 11px;
   color: #8a8a8a;
`;

export const ColumnTotal = styled.span`
   font-weight: 700;
   color: #555;
   font-variant-numeric: tabular-nums;
`;

export const ColumnBody = styled.div`
   flex: 1;
   min-height: 0;
   overflow-y: auto;
   padding: 10px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

export const ColumnEmpty = styled.div`
   padding: 18px 12px;
   text-align: center;
   font-size: 12px;
   color: #9a9a9a;
   line-height: 1.5;
`;

// Aviso de rodapé quando existe mais de uma página no backend (kanban não
// pagina por coluna — IMPLEMENTATION_GUIDE §3).
export const ColumnFootNote = styled.div`
   padding: 8px 12px 10px;
   text-align: center;
   font-size: 11px;
   color: #9a9a9a;
   border-top: 1px dashed rgba(0, 0, 0, 0.1);
`;

// Faixa de fecho da coluna (2026-08-31). Duas funções:
//
// 1. **Fecha o canto.** A `Column` tem raio 12 mas não recorta o conteúdo (não
//    pode: `overflow: hidden` mataria o scroll do corpo). Sem esta faixa, a
//    barra de rolagem do `ColumnBody` desce até a borda e o canto inferior
//    direito fica quadrado, brigando com o arredondamento do container.
// 2. **Dá respiro ao último card**, que antes encostava no fim da coluna.
//
// `background: inherit` de propósito: acompanha o realce de drop da `Column`
// (#fbfbfc → #f2f8fe) sem repetir a condicional aqui.
//
// `position: relative` + `z-index` não são decoração: sem eles a sombra pode
// ser pintada por baixo do conteúdo rolado, já que os cards são irmãos
// anteriores e não posicionados.
export const ColumnFootBar = styled.div`
   flex: 0 0 8px;
   position: relative;
   z-index: 1;
   border-radius: 0 0 11px 11px;
   background: inherit;
   box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
`;

// ─── Card do caso ─────────────────────────────────────────────────────────

export const CaseCard = styled.div`
   background: #fff;
   border: 1px solid
      ${({ $dragging }) =>
         $dragging ? 'rgba(64, 152, 239, 0.7)' : CARD_BORDER};
   border-radius: 12px;
   padding: 12px;
   display: flex;
   flex-direction: column;
   gap: 8px;
   cursor: pointer;
   box-shadow: ${CARD_SHADOW};
   opacity: ${({ $dragging }) => ($dragging ? 0.45 : 1)};
   transition: border-color 120ms ease;

   &:hover {
      border-color: rgba(64, 152, 239, 0.55);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
   }
`;

export const CaseTopRow = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 8px;
`;

export const CaseIdentity = styled.div`
   flex: 1;
   min-width: 0;
`;

export const CaseName = styled.div`
   font-size: 13px;
   font-weight: 700;
   line-height: 1.25;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const CaseDoc = styled.div`
   font-size: 11px;
   color: #8a8a8a;
   margin-top: 2px;
   font-variant-numeric: tabular-nums;
`;

// Avatar de iniciais do responsável — tracejado cinza quando não há.
export const Avatar = styled.span`
   width: ${({ $size }) => $size ?? 26}px;
   height: ${({ $size }) => $size ?? 26}px;
   flex: 0 0 ${({ $size }) => $size ?? 26}px;
   border-radius: 999px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: ${({ $size }) => (($size ?? 26) <= 22 ? 9 : 10)}px;
   font-weight: 800;
   background: ${({ $empty, theme }) =>
      $empty ? '#eee' : theme?.colors?.primary ?? '#4098ef'};
   color: ${({ $empty }) => ($empty ? '#aaa' : '#fff')};
   border: ${({ $empty }) =>
      $empty ? '1px dashed rgba(0, 0, 0, 0.15)' : 'none'};
`;

// Variante do avatar vazio sobre o cabeçalho escuro do drawer.
export const AvatarOnDark = styled(Avatar)`
   background: ${({ $empty, theme }) =>
      $empty ? 'rgba(255, 255, 255, 0.1)' : theme?.colors?.primary ?? '#4098ef'};
   color: ${({ $empty }) => ($empty ? '#8a8a8a' : '#fff')};
   border: ${({ $empty }) =>
      $empty ? '1px dashed rgba(255, 255, 255, 0.25)' : 'none'};
`;

export const CaseAmountRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 8px;
`;

export const CaseAmount = styled.b`
   font-size: 16px;
   font-weight: 800;
   font-variant-numeric: tabular-nums;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const CaseCount = styled.span`
   font-size: 11px;
   color: #666;
   white-space: nowrap;
`;

export const CasePillsRow = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
`;
